/**
 * FrameworkSummaryCard
 * 
 * Visual summary card for completed framework sessions.
 * Shows structured data, formula calculations, and visual breakdown.
 * 
 * This component displays the OUTPUT of a framework execution,
 * NOT the raw context or internal state.
 */

import React from 'react';
import { sanitizeToolData } from '../utils/outputSanitizer';

/**
 * Formula result display with visual breakdown
 */
const FormulaDisplay = ({ formula }) => {
  if (!formula) return null;
  
  return (
    <div className="formula-display">
      <div className="formula-header">
        <span className="formula-icon">📊</span>
        <h4>{formula.name}</h4>
      </div>
      
      <div className="formula-result">
        <span className="result-label">{formula.label}:</span>
        <span className="result-value">{formula.result}</span>
      </div>
      
      {formula.breakdown && formula.breakdown.length > 0 && (
        <div className="formula-breakdown">
          <p className="breakdown-label">Breakdown:</p>
          <div className="breakdown-items">
            {formula.breakdown.map((item, idx) => (
              <div key={idx} className="breakdown-item">
                <span className="breakdown-var">{item.variable.replace(/_/g, ' ')}:</span>
                <span className="breakdown-val">{item.value}</span>
              </div>
            ))}
          </div>
          {formula.expression && (
            <p className="formula-expression">
              Formula: {formula.expression}
            </p>
          )}
        </div>
      )}
      
      {formula.description && (
        <p className="formula-description">{formula.description}</p>
      )}
    </div>
  );
};

/**
 * Section display with icon and items
 */
const SummarySection = ({ section }) => {
  if (!section || !section.items || section.items.length === 0) return null;
  
  return (
    <div className="summary-section">
      <h4 className="section-title">
        <span className="section-icon">{section.icon}</span>
        {section.title}
      </h4>
      <div className="section-items">
        {section.items.map((item, idx) => (
          <div key={idx} className="section-item">
            <span className="item-label">{item.label}:</span>
            <span className="item-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Completion badge
 */
const CompletionBadge = ({ percentage, isComplete }) => {
  return (
    <div className={`completion-badge ${isComplete ? 'complete' : 'in-progress'}`}>
      {isComplete ? (
        <>
          <span className="badge-icon">✅</span>
          <span className="badge-text">Complete</span>
        </>
      ) : (
        <>
          <span className="badge-icon">⏳</span>
          <span className="badge-text">{percentage}% Complete</span>
        </>
      )}
    </div>
  );
};

/**
 * Main Summary Card Component
 */
const FrameworkSummaryCard = ({ summaryCard, onExport, onContinue }) => {
  if (!summaryCard) return null;
  
  // Sanitize the data
  const sanitized = sanitizeToolData(summaryCard);
  
  const {
    framework_name,
    icon = '🎯',
    is_complete = false,
    completion_percentage = 0,
    sections = [],
    formula
  } = sanitized;
  
  return (
    <div className="framework-summary-card">
      <div className="card-header">
        <div className="header-title">
          <span className="header-icon">{icon}</span>
          <h3>{framework_name}</h3>
        </div>
        <CompletionBadge 
          percentage={completion_percentage} 
          isComplete={is_complete} 
        />
      </div>
      
      <div className="card-content">
        {/* Sections */}
        {sections.map((section, idx) => (
          <SummarySection key={idx} section={section} />
        ))}
        
        {/* Formula Display */}
        {formula && <FormulaDisplay formula={formula} />}
      </div>
      
      {/* Action buttons */}
      <div className="card-actions">
        {is_complete && onExport && (
          <button 
            className="action-btn export-btn"
            onClick={onExport}
          >
            📤 Export Summary
          </button>
        )}
        {!is_complete && onContinue && (
          <button 
            className="action-btn continue-btn"
            onClick={onContinue}
          >
            ▶️ Continue Framework
          </button>
        )}
      </div>
      
      <style jsx>{`
        .framework-summary-card {
          background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
          border-radius: 16px;
          padding: 24px;
          margin: 16px 0;
          border: 1px solid rgba(78, 205, 196, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(78, 205, 196, 0.2);
        }
        
        .header-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .header-icon {
          font-size: 28px;
        }
        
        .header-title h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #4ECDC4;
        }
        
        .completion-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }
        
        .completion-badge.complete {
          background: rgba(46, 204, 113, 0.2);
          color: #2ecc71;
          border: 1px solid rgba(46, 204, 113, 0.4);
        }
        
        .completion-badge.in-progress {
          background: rgba(241, 196, 15, 0.2);
          color: #f1c40f;
          border: 1px solid rgba(241, 196, 15, 0.4);
        }
        
        .card-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .summary-section {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          padding: 16px;
        }
        
        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 12px 0;
          font-size: 15px;
          font-weight: 600;
          color: #ffffff;
        }
        
        .section-icon {
          font-size: 18px;
        }
        
        .section-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .section-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .section-item:last-child {
          border-bottom: none;
        }
        
        .item-label {
          color: #a0a0a0;
          font-size: 13px;
          flex-shrink: 0;
          margin-right: 12px;
        }
        
        .item-value {
          color: #ffffff;
          font-size: 14px;
          text-align: right;
          word-break: break-word;
        }
        
        .formula-display {
          background: linear-gradient(145deg, rgba(78, 205, 196, 0.1) 0%, rgba(78, 205, 196, 0.05) 100%);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(78, 205, 196, 0.3);
        }
        
        .formula-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 16px;
        }
        
        .formula-header h4 {
          margin: 0;
          color: #4ECDC4;
          font-size: 16px;
        }
        
        .formula-result {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(0, 0, 0, 0.3);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        
        .result-label {
          color: #a0a0a0;
          font-size: 14px;
        }
        
        .result-value {
          font-size: 24px;
          font-weight: 700;
          color: #4ECDC4;
        }
        
        .formula-breakdown {
          margin-top: 16px;
        }
        
        .breakdown-label {
          color: #a0a0a0;
          font-size: 12px;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .breakdown-items {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 8px;
        }
        
        .breakdown-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 6px;
          font-size: 13px;
        }
        
        .breakdown-var {
          color: #a0a0a0;
          text-transform: capitalize;
        }
        
        .breakdown-val {
          color: #ffffff;
          font-weight: 500;
        }
        
        .formula-expression {
          margin-top: 12px;
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 6px;
          font-family: 'SF Mono', 'Fira Code', monospace;
          font-size: 12px;
          color: #a0a0a0;
        }
        
        .formula-description {
          margin-top: 12px;
          color: #a0a0a0;
          font-size: 13px;
          font-style: italic;
        }
        
        .card-actions {
          display: flex;
          gap: 12px;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid rgba(78, 205, 196, 0.2);
        }
        
        .action-btn {
          flex: 1;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .export-btn {
          background: linear-gradient(135deg, #4ECDC4 0%, #44a08d 100%);
          color: #ffffff;
        }
        
        .export-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(78, 205, 196, 0.4);
        }
        
        .continue-btn {
          background: rgba(78, 205, 196, 0.1);
          color: #4ECDC4;
          border: 1px solid rgba(78, 205, 196, 0.3);
        }
        
        .continue-btn:hover {
          background: rgba(78, 205, 196, 0.2);
        }
      `}</style>
    </div>
  );
};

export default FrameworkSummaryCard;

